import _ from 'underscore';

import notifications from 'notifications';
import {BenchlingDispatch, Thunk} from 'redux-stores/Store';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import {RequestTask, ServerRequestTask, ServerUIRequestTask} from './RequestTaskConstants';
import * as RequestTaskDAO from './RequestTaskDAO';

export enum RequestTaskActionType {
  REQUEST_TASKS_FETCHED_FOR_REQUESTS = 'REQUEST_TASKS_FETCHED_FOR_REQUESTS',
  REQUEST_TASKS_FETCHED = 'REQUEST_TASKS_FETCHED',
  REQUEST_TASK_CREATED = 'REQUEST_TASK_CREATED',
  REQUEST_TASK_UPDATED = 'REQUEST_TASK_UPDATED',
  REQUEST_TASK_DELETED = 'REQUEST_TASK_DELETED',
  REQUEST_TASK_SAMPLE_GROUP_ASSOCIATIONS_UPDATED = 'REQUEST_TASK_SAMPLE_GROUP_ASSOCIATIONS_UPDATED',
}

interface RequestTasksFetchedForRequestsAction {
  type: RequestTaskActionType.REQUEST_TASKS_FETCHED_FOR_REQUESTS;
  taskIdsByRequestId: {[requestId: string]: Array<string>};
  sampleGroupIdsByRequestTaskId: {[requestTaskId: string]: Array<string>};
}
export function requestTasksFetchedForRequests(
  taskIdsByRequestId: {[requestId: string]: Array<string>},
  sampleGroupIdsByRequestTaskId: {[requestTaskId: string]: Array<string>}
): RequestTasksFetchedForRequestsAction {
  return {
    type: RequestTaskActionType.REQUEST_TASKS_FETCHED_FOR_REQUESTS,
    taskIdsByRequestId,
    sampleGroupIdsByRequestTaskId,
  };
}

export function fetchTasksForRequests(requestIds: Array<string>): Thunk<Promise<void>> {
  return async (dispatch: BenchlingDispatch) => {
    const {requestTasks, sampleGroupIdsByRequestTaskId} = await RequestTaskDAO.listRequestTasks(requestIds);
    const taskIdsByRequestId = {
      // Include a default empty list for all requests, in case the server didn't return any tasks for them
      ..._.object(requestIds.map((requestId) => [requestId, []])),
      // Merge the tasks the server returned
      ..._.chain(requestTasks)
        .groupBy('requestId') // eslint-disable-next-line no-restricted-syntax
        .mapObject((tasks) => _.pluck(tasks, 'id'))
        .value(),
    } as unknown as {[requestId: string]: Array<string>};

    dispatch(requestTasksFetched(requestTasks));
    dispatch(requestTasksFetchedForRequests(taskIdsByRequestId, sampleGroupIdsByRequestTaskId));
  };
}

export interface RequestTasksFetchedAction {
  type: RequestTaskActionType.REQUEST_TASKS_FETCHED;
  requestTasks: Array<RequestTask>;
}

export function requestTasksFetched(requestTasks: Array<RequestTask>): RequestTasksFetchedAction {
  return {
    type: RequestTaskActionType.REQUEST_TASKS_FETCHED,
    requestTasks,
  };
}

export interface RequestTaskCreatedAction {
  type: RequestTaskActionType.REQUEST_TASK_CREATED;
  requestTask: RequestTask;
  requestTaskIdsByRequestId: {[requestId: string]: Array<string>};
}
export function requestTaskCreated({
  requestTask,
  requestTaskIdsByRequestId,
}: {
  requestTask: RequestTask;
  requestTaskIdsByRequestId: {[requestId: string]: Array<string>};
}): RequestTaskCreatedAction {
  return {
    type: RequestTaskActionType.REQUEST_TASK_CREATED,
    requestTask,
    requestTaskIdsByRequestId,
  };
}

export function createRequestTask(requestId: string, requestTask: ServerUIRequestTask): Thunk<void> {
  return async (dispatch) => {
    const {requestTask: newRequestTask, requestTaskIdsByRequestId} = await RequestTaskDAO.createRequestTask(
      requestId,
      requestTask
    );
    dispatch(requestTaskCreated({requestTask: newRequestTask, requestTaskIdsByRequestId}));
    notifications.success('Task created successfully.');
  };
}

export interface RequestTaskUpdatedAction {
  type: RequestTaskActionType.REQUEST_TASK_UPDATED;
  updatedRequestTask: RequestTask;
}
export function requestTaskUpdated(updatedRequestTask: RequestTask): RequestTaskUpdatedAction {
  return {
    type: RequestTaskActionType.REQUEST_TASK_UPDATED,
    updatedRequestTask,
  };
}

export function updateRequestTask(newRequestTask: ServerRequestTask): Thunk<void> {
  return async (dispatch) => {
    const {requestTask: updatedRequestTask} = await RequestTaskDAO.updateRequestTask(newRequestTask);
    dispatch(requestTaskUpdated(updatedRequestTask));
    notifications.success('Task updated successfully.');
  };
}

export interface RequestTaskDeletedAction {
  type: RequestTaskActionType.REQUEST_TASK_DELETED;
  requestTaskId: string;
}
export function requestTaskDeleted(requestTaskId: string): RequestTaskDeletedAction {
  return {
    type: RequestTaskActionType.REQUEST_TASK_DELETED,
    requestTaskId,
  };
}

export function deleteRequestTask(requestTaskId: string): Thunk<void> {
  return async (dispatch) => {
    try {
      LoadingScreen.show();
      await RequestTaskDAO.deleteRequestTask(requestTaskId);
      dispatch(requestTaskDeleted(requestTaskId));
    } finally {
      LoadingScreen.hide();
    }
  };
}

export interface RequestTaskSampleAssociationsUpdatedAction {
  type: RequestTaskActionType.REQUEST_TASK_SAMPLE_GROUP_ASSOCIATIONS_UPDATED;
  sampleGroupIdsByRequestTaskId: {[requestTaskId: string]: Array<string>};
}
export function requestTaskSampleGroupAssociationsUpdated(sampleGroupIdsByRequestTaskId: {
  [requestTaskId: string]: Array<string>;
}): RequestTaskSampleAssociationsUpdatedAction {
  return {
    type: RequestTaskActionType.REQUEST_TASK_SAMPLE_GROUP_ASSOCIATIONS_UPDATED,
    sampleGroupIdsByRequestTaskId,
  };
}

export type RequestTaskAction =
  | RequestTasksFetchedForRequestsAction
  | RequestTasksFetchedAction
  | RequestTaskCreatedAction
  | RequestTaskUpdatedAction
  | RequestTaskDeletedAction
  | RequestTaskSampleAssociationsUpdatedAction;
